import React from 'react';
import { logger } from '@sentry/react';
import { LanguageCode } from '@visikon/core-models/languageTypes';
import { useFetchSynthesizedAudio } from 'api/v2/text2speechApi';
import { Button, Icon } from 'semantic-ui-react';
import { usePreferredVoice } from './UsePreferredVoice';
import { LexemeEntry, LexemeTypesEnum } from '@visikon/core-models';

type PhoneticsAudioButtonProps = {
  text: string;
  graphemeText: string;
  phoneme: string;
  // When set, the grapheme is previewed as a say-as alias instead of with the phoneme
  sayAs?: string;
  language: LanguageCode;
  selectedVoiceId: string;
  disabled: boolean;
  onPhonemeSanitized: (phoneme: string) => void;
};

function getGenerationKey({
  text,
  graphemeText,
  phoneme,
  sayAs,
  language,
  voiceId,
}: {
  text: string;
  graphemeText: string;
  phoneme: string;
  sayAs?: string;
  language: LanguageCode;
  voiceId: string;
}) {
  return JSON.stringify({ text, graphemeText, phoneme, sayAs, language, voiceId });
}

export function PhoneticsAudioButton({
  text,
  graphemeText,
  phoneme,
  sayAs,
  language,
  selectedVoiceId,
  disabled,
  onPhonemeSanitized,
}: PhoneticsAudioButtonProps) {
  const sanitizedPhoneme = phoneme.toLowerCase();
  const { voiceId } = usePreferredVoice({
    selectedLanguage: language,
    selectedVoiceId,
  });

  const currentGenerationKey = React.useMemo(
    () => getGenerationKey({ text, graphemeText, phoneme: sanitizedPhoneme, sayAs, language, voiceId }),
    [text, graphemeText, language, sanitizedPhoneme, sayAs, voiceId],
  );

  const mediaRef = React.useRef<HTMLAudioElement>(null);
  const objectUrlRef = React.useRef('');
  const pendingGenerationKeyRef = React.useRef('');
  const currentGenerationKeyRef = React.useRef(currentGenerationKey);
  const [lastGeneratedKey, setLastGeneratedKey] = React.useState('');
  const [isPlaying, setPlaying] = React.useState(false);

  const lexiconOverride = React.useMemo(() => {
    const lexiconMap = new Map<string, LexemeEntry[]>();
    const graphemeLower = graphemeText.toLowerCase();
    const overrideEntry = { id: 'override-entry', grapheme: graphemeLower, description: '', stem: '' };

    lexiconMap.set(graphemeLower, [
      sayAs !== undefined
        ? { ...overrideEntry, type: LexemeTypesEnum.SAY_AS, sayAs }
        : { ...overrideEntry, type: LexemeTypesEnum.PHONETIC, phoneme: sanitizedPhoneme },
    ]);
    return lexiconMap;
  }, [graphemeText, sanitizedPhoneme, sayAs]);

  React.useEffect(() => {
    currentGenerationKeyRef.current = currentGenerationKey;
  }, [currentGenerationKey]);

  React.useEffect(
    () => () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    },
    [],
  );

  React.useEffect(() => {
    if (isPlaying && lastGeneratedKey !== currentGenerationKey) {
      mediaRef.current?.pause();
      setPlaying(false);
    }
  }, [currentGenerationKey, isPlaying, lastGeneratedKey]);

  const playCurrentAudio = React.useCallback(async () => {
    const mediaTag = mediaRef.current;

    if (!mediaTag?.src) {
      return;
    }

    if (mediaTag.ended) {
      mediaTag.currentTime = 0;
    }

    try {
      await mediaTag.play();
      setPlaying(true);
    } catch (error) {
      setPlaying(false);
      logger.error('Failed to play generated phonetics audio:', error);
    }
  }, []);

  const handleSynthesizedAudioSuccess = React.useCallback(
    (blob: Blob) => {
      const mediaTag = mediaRef.current;
      const generatedKey = pendingGenerationKeyRef.current;

      if (!blob || !mediaTag || !generatedKey) {
        return;
      }

      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }

      const customFileName = `Generated_Audio_Phonetics_test.wav`;
      const file = new File([blob], customFileName, { type: blob.type });
      const tempUrl = URL.createObjectURL(file);

      objectUrlRef.current = tempUrl;
      mediaTag.src = tempUrl;
      pendingGenerationKeyRef.current = '';
      setLastGeneratedKey(generatedKey);

      if (generatedKey === currentGenerationKeyRef.current) {
        playCurrentAudio();
      }
    },
    [playCurrentAudio],
  );

  const synthesizedTestSpeak = useFetchSynthesizedAudio({
    text,
    activeLanguage: language,
    selectedVoiceId: voiceId || undefined,
    lexiconOverride,
    onSuccess: handleSynthesizedAudioSuccess,
  });

  const hasCurrentGeneratedAudio = !!objectUrlRef.current && lastGeneratedKey === currentGenerationKey;

  const stopCurrentAudio = React.useCallback(() => {
    const mediaTag = mediaRef.current;

    if (mediaTag) {
      mediaTag.pause();
      mediaTag.currentTime = 0;
    }

    setPlaying(false);
  }, []);

  const handleButtonClicked = () => {
    if (disabled || synthesizedTestSpeak.isLoading) {
      return;
    }

    if (hasCurrentGeneratedAudio) {
      if (isPlaying) {
        mediaRef.current?.pause();
        setPlaying(false);
        return;
      }

      playCurrentAudio();
      return;
    }

    onPhonemeSanitized(sanitizedPhoneme);
    stopCurrentAudio();
    pendingGenerationKeyRef.current = currentGenerationKey;
    synthesizedTestSpeak.mutate(undefined, {
      onError: () => {
        pendingGenerationKeyRef.current = '';
        setPlaying(false);
      },
    });
  };

  let iconName: React.ComponentProps<typeof Icon>['name'] = 'sync alternate';

  if (hasCurrentGeneratedAudio) {
    iconName = isPlaying ? 'pause circle' : 'play circle';
  }

  return (
    <>
      <Button
        name={hasCurrentGeneratedAudio ? 'play' : 'generate'}
        onClick={handleButtonClicked}
        disabled={disabled || synthesizedTestSpeak.isLoading}
        loading={synthesizedTestSpeak.isLoading}
      >
        <Icon name={iconName} />
        {!hasCurrentGeneratedAudio && !isPlaying && 'Generate and '}
        {isPlaying ? 'Pause' : 'Play'}
      </Button>
      <audio ref={mediaRef} style={{ display: 'none' }} onEnded={() => setPlaying(false)} preload="metadata" crossOrigin="anonymous" />
    </>
  );
}
